import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config/api";

const API_URL = `${API_BASE_URL}/api`;

export default function TaskListDialog({ open, onClose, selectedDate, onTaskUpdated }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eligibleStaff, setEligibleStaff] = useState([]);
  const [assigningTask, setAssigningTask] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [assignmentNotes, setAssignmentNotes] = useState("");

  useEffect(() => {
    if (open && selectedDate) {
      fetchTasks();
      fetchEligibleStaff();
    }
  }, [open, selectedDate]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const dateStr = selectedDate.toISOString().split("T")[0];
      const response = await axios.get(`${API_URL}/tasks/date/${dateStr}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        // Flatten tasks from bills and sort by priority
        const allTasks = [];
        response.data.bills.forEach((bill) => {
          bill.tasks.forEach((task) => {
            allTasks.push({
              ...task,
              BillNumber: bill.billNumber,
              CustomerName: bill.customerName,
              CustomerPhone: bill.customerPhone,
            });
          });
        });
        
        // Sort by priority: High (1) -> Medium (2) -> Low (3)
        allTasks.sort((a, b) => {
          const priorityOrder = { High: 1, Medium: 2, Low: 3 };
          return priorityOrder[a.Priority] - priorityOrder[b.Priority];
        });
        
        setTasks(allTasks);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleStaff = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/tasks/eligible-staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setEligibleStaff(response.data.staff);
      }
    } catch (error) {
      console.error("Error fetching eligible staff:", error);
    }
  };

  const handleAssignTask = async (taskId) => {
    if (!selectedStaff) {
      alert("Please select a staff member");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/tasks/assign`,
        {
          taskId,
          staffId: selectedStaff,
          assignmentNotes,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Task assigned successfully!");
      setAssigningTask(null);
      setSelectedStaff("");
      setAssignmentNotes("");
      fetchTasks();
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      console.error("Error assigning task:", error);
      alert("Failed to assign task");
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "#FFEBEE"; // Light red
      case "Medium":
        return "#E3F2FD"; // Light blue
      case "Low":
        return "#FFF9C4"; // Light yellow
      default:
        return "#fff";
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "High":
        return { bg: "#F44336", text: "#fff" }; // Red
      case "Medium":
        return { bg: "#1976D2", text: "#fff" }; // Blue
      case "Low":
        return { bg: "#F57C00", text: "#fff" }; // Orange
      default:
        return { bg: "#757575", text: "#fff" };
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#8B7355" }}>
            Tasks for {selectedDate && formatDate(selectedDate)}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress sx={{ color: "#C4A574" }} />
          </Box>
        ) : tasks.length === 0 ? (
          <Typography sx={{ textAlign: "center", p: 4, color: "#666" }}>
            No tasks for this date
          </Typography>
        ) : (
          <Box>
            {/* Summary */}
            <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#8B7355", mb: 1 }}>
                Total Tasks: {tasks.length}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#F44336" }} />
                  <Typography variant="body2">
                    High: {tasks.filter(t => t.Priority === "High").length}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#1976D2" }} />
                  <Typography variant="body2">
                    Medium: {tasks.filter(t => t.Priority === "Medium").length}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#F57C00" }} />
                  <Typography variant="body2">
                    Low: {tasks.filter(t => t.Priority === "Low").length}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Task Cards */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {tasks.map((task) => {
                const priorityBadge = getPriorityBadgeColor(task.Priority);
                
                return (
                  <Paper
                    key={task.TaskId}
                    elevation={3}
                    sx={{
                      p: 2.5,
                      bgcolor: getPriorityColor(task.Priority),
                      border: `2px solid ${priorityBadge.bg}`,
                      borderRadius: 2,
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    {/* Priority Badge */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                      <Box
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: priorityBadge.bg,
                          color: priorityBadge.text,
                          fontWeight: 700,
                          fontSize: "0.85rem",
                        }}
                      >
                        {task.Priority.toUpperCase()} PRIORITY
                      </Box>
                      
                      {task.Status === "Pending" && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setAssigningTask(task.TaskId)}
                          sx={{
                            bgcolor: "#8B7355",
                            "&:hover": { bgcolor: "#6D5A43" },
                            fontWeight: 600,
                          }}
                        >
                          Assign Task
                        </Button>
                      )}
                    </Box>

                    {/* Task Details */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#8B7355", mb: 1 }}>
                        {task.TaskType}: {task.ProductName}
                      </Typography>
                      
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1.5 }}>
                        <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
                          <strong>Size:</strong> {task.ProductSize}
                        </Typography>
                        <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
                          <strong>Description:</strong> {task.Description}
                        </Typography>
                      </Box>

                      {/* Bill & Customer Info */}
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "rgba(255, 255, 255, 0.7)",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.85rem", color: "#555", mb: 0.5 }}>
                          <strong>Bill:</strong> {task.BillNumber}
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem", color: "#555", mb: 0.5 }}>
                          <strong>Customer:</strong> {task.CustomerName}
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>
                          <strong>Phone:</strong> {task.CustomerPhone}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Status & Assignment Info */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: task.Status === "Completed" ? "#4CAF50" : 
                                   task.Status === "InProgress" ? "#FF9800" : 
                                   task.Status === "Assigned" ? "#2196F3" : "#9E9E9E",
                          color: "white",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        {task.Status}
                      </Box>
                      
                      {task.AssignedStaffName && (
                        <Typography sx={{ fontSize: "0.85rem", color: "#4caf50", fontWeight: 600 }}>
                          ✓ Assigned to: {task.AssignedStaffName}
                        </Typography>
                      )}
                    </Box>

                    {/* Assignment Form */}
                    {assigningTask === task.TaskId && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: "white", borderRadius: 1, border: "2px solid #8B7355" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#8B7355", mb: 2 }}>
                          Assign Task to Staff
                        </Typography>
                        
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Select Staff</InputLabel>
                          <Select
                            value={selectedStaff}
                            onChange={(e) => setSelectedStaff(e.target.value)}
                            label="Select Staff"
                          >
                            {eligibleStaff.map((staff) => (
                              <MenuItem key={staff.StaffId} value={staff.StaffId}>
                                {staff.FullName} ({staff.ActiveTaskCount} active tasks)
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Assignment Notes (Optional)"
                          value={assignmentNotes}
                          onChange={(e) => setAssignmentNotes(e.target.value)}
                          sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="contained"
                            onClick={() => handleAssignTask(task.TaskId)}
                            sx={{
                              bgcolor: "#4caf50",
                              "&:hover": { bgcolor: "#45a049" },
                              fontWeight: 600,
                            }}
                          >
                            Confirm Assignment
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setAssigningTask(null);
                              setSelectedStaff("");
                              setAssignmentNotes("");
                            }}
                            sx={{
                              borderColor: "#8B7355",
                              color: "#8B7355",
                              "&:hover": {
                                borderColor: "#6D5A43",
                                bgcolor: "rgba(139, 115, 85, 0.04)",
                              },
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
